import React, { useEffect } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Close } from '@mui/icons-material';
import { IVideoManagementPageProps, VideoManagementPage } from './VideoManagementPage';
import { IVideoSubmission, IVideoManagementFilters, IPaginationInfo } from './types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

export interface IVideoManagementModalProps extends Pick<IVideoManagementPageProps, 'onPreselect'> {
    open: boolean;
    onClose: () => void;
    contestId: string;
    submissions: IVideoSubmission[];
    pagination: IPaginationInfo | null;
    loading: boolean;
    error: string | null;
    selectedVideos: string[];
    onVideoSelect: (videoId: string, checked: boolean) => void;
    onSelectAll: () => void;
    onSendEmail: () => void;
    onFiltersChange: (filters: Partial<IVideoManagementFilters>) => void;
    filters: IVideoManagementFilters;
}

export const VideoManagementModal: React.FC<IVideoManagementModalProps> = ({
    open,
    onClose,
    contestId,
    submissions,
    pagination,
    loading,
    error,
    selectedVideos,
    onVideoSelect,
    onSelectAll,
    onSendEmail,
    onFiltersChange,
    filters,
    onPreselect,
}) => {
    const themeOs = useTheme();
    const fullScreen = useMediaQuery(themeOs.breakpoints.down('md'));
    const { themeStyle } = useThemeContext();

    // Reset selected videos when modal opens
    useEffect(() => {
        if (open) {
            // Reset selection when modal opens
            // This could be handled by the parent component if needed
        }
    }, [open]);

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={fullScreen}
            maxWidth={false}
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    margin: fullScreen ? 0 : '32px',
                    maxWidth: fullScreen ? '100%' : '95vw',
                    maxHeight: fullScreen ? '100%' : '95vh',
                    width: fullScreen ? '100%' : '95vw',
                    height: fullScreen ? '100%' : '95vh',
                },
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: theme[themeStyle].deep_grey,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}>
                <Typography variant="h5" component="h2" fontWeight="bold" sx={{ color: theme[themeStyle].tag_white }}>
                    Contest Submission Management
                </Typography>
                <IconButton
                    onClick={handleClose}
                    size="large"
                    sx={{
                        color: 'text.secondary',
                        '&:hover': {
                            backgroundColor: 'action.hover',
                        },
                    }}>
                    <Close sx={{ color: theme[themeStyle].tag_white }} />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent
                sx={{
                    p: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}>
                <VideoManagementPage
                    submissions={submissions}
                    pagination={pagination}
                    loading={loading}
                    error={error}
                    selectedVideos={selectedVideos}
                    onVideoSelect={onVideoSelect}
                    onSelectAll={onSelectAll}
                    onSendEmail={onSendEmail}
                    onFiltersChange={onFiltersChange}
                    filters={filters}
                    onPreselect={onPreselect}
                />
            </DialogContent>
        </Dialog>
    );
};
